import { useState, type CSSProperties, type ReactNode } from "react";
import { mediaUrl } from "../../mediaAuth";

const MAX_AUTO_RETRIES = 2;

// Every avatar <img> in the app shares the exact stale-media-token failure
// mode MessageBubble.tsx's chat photo attachments already had to solve: the
// token baked into src at render time (mediaAuth.ts's mediaUrl()) can go
// stale - tab backgrounded, token refreshed since this mounted - with
// nothing re-rendering the <img> in between to pick up a fresh one. Plain
// <img src={mediaUrl(...)}> with no onError handling (what ProfileDialog.tsx/
// PresenceIndicator.tsx/ChatListScreen.tsx each had before this) just shows
// a permanently broken-image icon when that race is lost. A couple of
// automatic retries (remounting with a freshly computed mediaUrl(), which
// reflects whatever token is current *now*) self-heals that; falling back
// to the caller's placeholder (initials, a person icon, whatever fits that
// call site) covers anything beyond that instead of ever leaving a bare
// broken-image icon on screen.
export function AvatarImage({
  filename,
  alt,
  style,
  fallback,
}: {
  filename: string;
  alt: string;
  style?: CSSProperties;
  fallback: ReactNode;
}) {
  const [retryKey, setRetryKey] = useState(0);
  const [failed, setFailed] = useState(false);

  if (failed) return <>{fallback}</>;

  return (
    <img
      key={retryKey}
      src={mediaUrl(`/assets/avatars/${filename}`)}
      alt={alt}
      style={style}
      onError={() => {
        if (retryKey < MAX_AUTO_RETRIES) setRetryKey((k) => k + 1);
        else setFailed(true);
      }}
    />
  );
}
